// @ts-strict-ignore
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';

import type {
  BehaviorMetricDisplay,
  FormulaMeasurement,
  FormulaReferencedMetrics,
  FormulaShowClause,
  MetricBehavior,
  ShowClause,
  ShowClauseFormula,
} from 'common/report/insights/util/types';
import type {FormulaShowClause as FormulaShowClauseClass} from 'common/report/insights/models/show-clause';
import type {MetricsResource} from 'common/report/model-resources/metrics';
import type {BehaviorsResource} from 'common/report/model-resources/behaviors';
import type {QueryFormulaEntry} from 'common/query-builder/query-entry-section/types';
import {MultiAttribution} from 'common/widgets/multi-attribution-menu/types';
import {BehaviorShowClause} from 'common/report/insights/util/types';
import * as BookmarkTypes from 'common/types/reports/bookmark';
import {MetricType, TopLevelMetricType} from 'common/types/reports/bookmark';
import {FormulaMetric} from 'app_api/projects/metrics/types';

import {ModelStatus} from './model-resource/model-resource';

// NOTE: The modification check doesn't handle the scenario where the user changes formula and the formula and
// then undoes the change (e.g. changing definition from 'A / B' -> 'B / A' -> 'A / B'). We could handle this
// scenario with an isEqual check, but I think it's easier to reason about (and safe from edge cases) to consider
// any user changes to have modified the saved formula.
// NOTE: this shouldn't be used with a resolved saved formula, as that will always have referencedMetrics field.
export function isModifiedShowClauseFormula(formula: BookmarkTypes.FormulaShowClause): boolean {
  // TODO(CC) - should also check if the saved behaviors in formula have been modified or not
  return `hasUnsavedChanges` in formula ? formula.hasUnsavedChanges : Boolean(formula.referencedMetrics);
}

/** local helper to hydrate saved behaviors and saved metrics in formula referenced metrics */
function getHydratedReferencedMetrics(
  referencedMetrics: BehaviorShowClause[],
  behaviorsResource: BehaviorsResource,
  metricsResource: MetricsResource,
): BehaviorShowClause[] {
  return referencedMetrics.map((clause) => {
    // if it's a saved metric that is not resolved (canonical metric), resolve it
    // A canonical metric has an id but is missing the behavior field
    if (clause.id && !clause.behavior) {
      const metric = metricsResource.getOne(clause.id).value;
      // Only handle BehaviorMetric types (not FormulaMetric)
      if (metric && metric.type === TopLevelMetricType.Metric) {
        return {
          ...metric.definition,
          type: TopLevelMetricType.Metric,
          id: metric.id,
          name: metric.name,
          display: metric.definition.display ?? {},
          measurement: metric.definition.measurement ?? {},
        } as BehaviorShowClause;
      }
    }

    // if it's a saved behavior clause that is not resolved, resolve it
    if (
      `behavior` in clause &&
      !!clause.behavior.id &&
      [MetricType.Funnel, MetricType.Retention].includes(clause.behavior.type) &&
      isNil(clause.behavior.behaviors)
    ) {
      const behavior = behaviorsResource.getOne(clause.behavior.id).value;
      if (!behavior) {
        return clause;
      }
      return {
        ...clause,
        behavior: {
          id: behavior.id,
          name: behavior.name,
          ...behavior.definition.behavior,
        } as MetricBehavior,
      };
    }
    return clause;
  });
}

/** local helper to keep formula expansion DRY */
function resolveFormulaHelper({
  metricsResource,
  behaviorsResource,
  formula,
  multiAttribution,
}: {
  metricsResource: MetricsResource;
  behaviorsResource: BehaviorsResource;
  formula: ShowClauseFormula;
  multiAttribution: MultiAttribution | undefined;
}): {
  pendingSavedFormulaStatus: null | ModelStatus;
  isUnmodifiedSavedFormula: boolean;
  unmodifiedSavedDisplayFields: BehaviorMetricDisplay;
  unmodifiedSavedMeasurementFields: FormulaMeasurement;
  unmodifiedSavedFormulaFields: Partial<ShowClauseFormula>;
} {
  const initialOutput = {
    pendingSavedFormulaStatus: null,
    isUnmodifiedSavedFormula: false,
    unmodifiedSavedDisplayFields: {},
    unmodifiedSavedMeasurementFields: {},
    unmodifiedSavedFormulaFields: {},
  };

  // not a saved formula
  if (!formula.id) {
    // There can be a case where the formula entry is not saved but has referenced metrics populated (when add inline formula in canvas)
    // If there are any referenced metrics, hydrate them.
    if (formula.referencedMetrics) {
      return {
        ...initialOutput,
        unmodifiedSavedFormulaFields: {
          referencedMetrics: getHydratedReferencedMetrics(
            formula.referencedMetrics,
            behaviorsResource,
            metricsResource,
          ),
        },
      };
    }
    return initialOutput;
  }

  const model = metricsResource.getOne(formula.id);
  if (model.status !== ModelStatus.Success) {
    return {...initialOutput, pendingSavedFormulaStatus: model.status};
  }

  const savedFormulaMetric = model.value as FormulaMetric;
  const measurement = savedFormulaMetric.definition.measurement;
  const display = savedFormulaMetric.definition.display;
  const {definition, referencedMetrics} = savedFormulaMetric.definition.formula;
  if (isModifiedShowClauseFormula(formula)) {
    const hydratedReferencedMetrics = getHydratedReferencedMetrics(
      formula.referencedMetrics,
      behaviorsResource,
      metricsResource,
    );
    return {
      ...initialOutput,
      unmodifiedSavedDisplayFields: display,
      unmodifiedSavedFormulaFields: {
        // get hydrated referenced metrics for saved behaviors and metrics
        referencedMetrics: hydratedReferencedMetrics,
      },
    };
  }

  return {
    pendingSavedFormulaStatus: null,
    isUnmodifiedSavedFormula: true,
    unmodifiedSavedDisplayFields: display,
    unmodifiedSavedMeasurementFields: {
      ...(measurement?.cumulative ? {cumulative: measurement.cumulative} : {}),
      ...(measurement?.rolling ? {rolling: measurement.rolling} : {}),
      // multiAttribution is local rather than part of the saved formula
      ...(multiAttribution && {multiAttribution}),
    },
    unmodifiedSavedFormulaFields: {
      id: savedFormulaMetric.id,
      name: savedFormulaMetric.name,
      definition,
      referencedMetrics: getHydratedReferencedMetrics(referencedMetrics, behaviorsResource, metricsResource),
    },
  };
}

/**
 * Returns QueryFormulaEntry (or a subset), adding the saved formula fields if the local formula is unmodified.
 *
 * Returns null if it's a saved formula that has not loaded
 */
export function getResolvedFormulaEntry<T extends Pick<QueryFormulaEntry, `event` | `multiAttribution`>>({
  metricsResource,
  behaviorsResource,
  formulaEntry,
}: {
  metricsResource: MetricsResource;
  behaviorsResource: BehaviorsResource;
  formulaEntry: T;
}): T extends QueryFormulaEntry ? QueryFormulaEntry : Pick<QueryFormulaEntry, `event` | `multiAttribution`> | null {
  if (!formulaEntry.event) {
    return null;
  }

  const {
    pendingSavedFormulaStatus,
    unmodifiedSavedDisplayFields,
    unmodifiedSavedMeasurementFields,
    unmodifiedSavedFormulaFields,
  } = resolveFormulaHelper({
    metricsResource,
    behaviorsResource,
    formula: formulaEntry.event,
    multiAttribution: formulaEntry.multiAttribution,
  });

  if (pendingSavedFormulaStatus) {
    return null;
  }

  return cloneDeep({
    ...formulaEntry,
    event: {
      ...formulaEntry.event,
      ...unmodifiedSavedFormulaFields,
    },
    display: unmodifiedSavedDisplayFields,
    ...unmodifiedSavedMeasurementFields,
  }) as T extends QueryFormulaEntry ? QueryFormulaEntry : Pick<QueryFormulaEntry, `event`>;
}

export function isValidFormulaShowClause(clause: ShowClause): clause is FormulaShowClause {
  if (clause.type !== TopLevelMetricType.Formula) {
    return false;
  }
  return isValidFormula(clause);
}

/** @todo(metrics) marked for deletion */
function isValidFormula(formula: ShowClauseFormula): boolean {
  if (formula.id) {
    return true;
  }

  return Boolean(formula);
}

/**
 * Returns FormulaShowClause (non-class type), adding the saved formula fields if the local formula is unmodified.
 *
 * Returns `null` if it's a saved formula that has not loaded
 */
export function getResolvedFormulaShowClause({
  metricsResource,
  behaviorsResource,
  formulaShowClause,
}: {
  metricsResource: MetricsResource;
  behaviorsResource: BehaviorsResource;
  formulaShowClause: FormulaShowClause;
}): FormulaShowClause | null {
  if (!isValidFormula(formulaShowClause)) {
    return null;
  }

  const {
    pendingSavedFormulaStatus,
    isUnmodifiedSavedFormula,
    unmodifiedSavedDisplayFields,
    unmodifiedSavedMeasurementFields,
    unmodifiedSavedFormulaFields,
  } = resolveFormulaHelper({
    metricsResource,
    behaviorsResource,
    formula: formulaShowClause,
    multiAttribution: formulaShowClause.measurement?.multiAttribution,
  });

  if (pendingSavedFormulaStatus) {
    return null;
  }

  if (!isUnmodifiedSavedFormula) {
    return cloneDeep(formulaShowClause);
  }

  return cloneDeep({
    ...formulaShowClause,
    ...unmodifiedSavedFormulaFields,
    ...(!isEmpty(unmodifiedSavedDisplayFields) ? {display: unmodifiedSavedDisplayFields} : {}),
    ...(!isEmpty(unmodifiedSavedMeasurementFields) ? {measurement: unmodifiedSavedMeasurementFields} : {}),
  });
}

/**
 * If the referenced metrics have changed, update the saved formula clause with these local modifications.
 */
export function maybeModifySavedFormula({
  formulaClassObject,
  metricsResource,
  behaviorsResource,
  updateReferencedMetricsFn,
}: {
  formulaClassObject: FormulaShowClauseClass;
  metricsResource: MetricsResource;
  behaviorsResource: BehaviorsResource;
  updateReferencedMetricsFn: (referencedMetrics: FormulaReferencedMetrics) => void;
}): void {
  const resolvedClause = getResolvedFormulaShowClause({
    formulaShowClause: formulaClassObject.toBookmarkData(),
    behaviorsResource,
    metricsResource,
  });
  if (!resolvedClause || !resolvedClause.id) {
    return;
  }

  const oldReferencedMetrics = cloneDeep(resolvedClause.referencedMetrics);
  updateReferencedMetricsFn(resolvedClause.referencedMetrics);
  if (!isEqual(oldReferencedMetrics, resolvedClause.referencedMetrics)) {
    formulaClassObject.id = resolvedClause.id;
    formulaClassObject.name = resolvedClause.name;
    formulaClassObject.definition = resolvedClause.definition;
    formulaClassObject.referencedMetrics = resolvedClause.referencedMetrics;
    formulaClassObject.measurement = resolvedClause.measurement;
  }
}
